import { useEffect, useState } from 'react';
import api from '@/lib/api';

interface Product {
  id: string;
  sku: string;
  name: string;
  category: string;
  brand: string;
  price: number;
  stocks: Array<{ quantity: number; reserved: number }>;
  active: boolean;
}

export default function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    category: '',
    brand: '',
    unit: 'UN',
    price: 0,
    cost: 0,
    minStock: 10,
    maxStock: 500,
  });

  useEffect(() => {
    const params = search ? `?search=${search}` : '';
    api.get(`/products${params}`).then((r) => {
      setProducts(r.data.data);
      setLoadingData(false);
    }).catch(() => setLoadingData(false));
  }, [search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.post('/products', formData);
    setShowModal(false);
    setFormData({ sku: '', name: '', description: '', category: '', brand: '', unit: 'UN', price: 0, cost: 0, minStock: 10, maxStock: 500 });
    api.get('/products').then((r) => setProducts(r.data.data));
  };

  return (
    <>
      <div className="flex items-center gap-4 mb-6">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar produto..."
          className="px-4 py-2 border border-dark-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
        >
          Novo Produto
        </button>
      </div>

      {loadingData ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-dark-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-dark-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Categoria</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Marca</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-dark-500 uppercase">Preço</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-dark-500 uppercase">Estoque Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-100">
              {products.map((product, index) => {
                const totalStock = product.stocks.reduce((acc, s) => acc + s.quantity, 0);
                return (
                  <tr key={product.id} className={index % 2 === 0 ? 'bg-white' : 'bg-dark-50/50'}>
                    <td className="px-6 py-4 text-sm font-medium text-primary-600">{product.sku}</td>
                    <td className="px-6 py-4 text-sm text-dark-900">{product.name}</td>
                    <td className="px-6 py-4 text-sm text-dark-600">{product.category || '-'}</td>
                    <td className="px-6 py-4 text-sm text-dark-600">{product.brand || '-'}</td>
                    <td className="px-6 py-4 text-sm font-medium text-dark-900 text-right">
                      {product.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-right">
                      <span className={totalStock > 0 ? 'text-green-600' : 'text-red-600'}>{totalStock}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${product.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {product.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {products.length === 0 && (
            <div className="text-center py-12 text-dark-500">Nenhum produto encontrado</div>
          )}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-bold text-dark-900 mb-4">Novo Produto</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">SKU</label>
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1">Categoria</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1">Marca</label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1">Preço</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1">Custo</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-dark-200 rounded-lg text-sm font-medium hover:bg-dark-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
                >
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
